import { ConverterExchangeProtocolEnum } from '@zaoblako/shared/modules/pacs/enums/converter-exchange-protocol.enum';
import { ConverterOperatingModeEnum } from '@zaoblako/shared/modules/pacs/enums/converter-operating-mode.enum';
import { ConverterReaderProtocolEnum } from '@zaoblako/shared/modules/pacs/enums/converter-reader-protocol.enum';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ConverterStatusEnum } from '@zaoblako/shared/modules/pacs/enums/converter-status.enum';
import { ConverterLockSettingsFindDto, type ConverterLockSettingsFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-lock-settings-find.dto';
import { ConverterInvertEventsDto, type ConverterInvertEventsInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-invert-events.dto';
import { ConverterTestMockFindDto, type ConverterTestMockFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-test-mock-find.dto';
import { ConverterIconLogicFindDto, type ConverterIconLogicFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-icon-logic-find.dto';
import { ConverterTimeZoneSingleFormDto, type ConverterTimeZoneSingleFormInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-timezone-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import { AccessCardFindDto, type AccessCardFindInterface } from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-find.dto';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { UserGroupFindDto, UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { IronLogicOperationEnum } from '@zaoblako/shared/modules/pacs/enums/iron-logic-operation.enum';
import { ConverterReadCardsFindDto, type ConverterReadCardsFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-read-cards-find.dto'
import { ConverterPingSettingsDto, type ConverterPingSettingsInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-ping-settings.dto';
import { CctvDtoKeysEnum } from '@zaoblako/shared/modules/cctv/enums/cctv-dto-keys.enum';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import type { HikvisionCameraFindInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hikvision-camera-find.dto';

import * as yup from 'yup';

/** Converter response body */
export interface ConverterFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Converter display name */
  name: string;
  /** Converter-FRM exchange protocol to use */
  exchangeProtocol: ConverterExchangeProtocolEnum;
  /** Converter card reader protocol */
  readerProtocol: ConverterReaderProtocolEnum;
  /** Converter lock settings */
  lockSettings?: ConverterLockSettingsFindInterface;
  /** Converter events invertion settings */
  invertEvents?: ConverterInvertEventsInterface;
  /** Cards reading settings */
  readCards?: ConverterReadCardsFindInterface;
  /** Card ping settings */
  pingSettings?: ConverterPingSettingsInterface;
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion update date and time. The converter will be permanently deleted six months after this date. */
  deleted?: Date;
  /** User who created this converter */
  createdBy?: UserFindInterface;
  /** Converter status protocol */
  status: ConverterStatusEnum;
  /** Converter room */
  room?: RoomFindInterface;
  /** Test mock converter data */
  testMock?: ConverterTestMockFindInterface;
  /** "Iron Logic" device data */
  ironLogic?: ConverterIconLogicFindInterface;
  /** Added access cards */
  accessCards?: AccessCardFindInterface[];
  accessCardsCount?: number;
  /** Groups */
  userGroups?: UserGroupFindInterface[];
  /** Converter last operation date */
  lastOperationDate?: Date;
  /** Converter last operation type */
  lastOperation?: IronLogicOperationEnum;
  /** Converter time zones */
  timeZones: ConverterTimeZoneSingleFormInterface[];
  /** Converter operating mode */
  operatingMode?: ConverterOperatingModeEnum;
  /** "Hikvision" CCTV cameras connected to this converter */
  cameras?: HikvisionCameraFindInterface[];
}

/** Converter response body - DTO schema */
export const ConverterFindDto = SerdeDtoClass.create<ConverterFindInterface>(
  PacsDtoKeysEnum.CONVERTER_FIND_DTO,
  {
    id: {
      label: 'Номер',
      serializer: yup.number(),
    },
    name: {
      label: 'Название',
      serializer: yup.string(),
    },
    exchangeProtocol: {
      label: 'Протокол обмена',
      serializer: yup
        .string()
        .oneOf(Object.values(ConverterExchangeProtocolEnum)),
    },
    readerProtocol: {
      label: 'Протокол считывателя карт',
      serializer: yup
        .string()
        .oneOf(Object.values(ConverterReaderProtocolEnum)),
    },
    lockSettings: {
      optional: true,
      label: 'Настройки блокировки',
      nested: ConverterLockSettingsFindDto,
      serializer: (_, nested) => nested,
    },
    invertEvents: {
      optional: true,
      label: 'Инверсия событий',
      nested: ConverterInvertEventsDto,
      serializer: (_, nested) => nested,
    },
    readCards: {
      optional: true,
      label: 'Синхронизация количества записанных карт',
      nested: ConverterReadCardsFindDto,
      serializer: (_, nested) => nested,
    },
    pingSettings: {
      optional: true,
      label: 'Настройки пинга',
      nested: ConverterPingSettingsDto,
      serializer: (_, nested) => nested,
    },
    created: {
      label: 'Создан',
      serializer: yup.date(),
    },
    updated: {
      label: 'Изменен',
      serializer: yup.date(),
    },
    deleted: {
      optional: true,
      label: 'Удален',
      serializer: yup.date(),
    },
    createdBy: {
      optional: true,
      label: 'Автор',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    status: {
      label: 'Статус',
      serializer: yup
        .string()
        .oneOf(Object.values(ConverterStatusEnum)),
    },
    testMock: {
      optional: true,
      label: 'Настройки для тестового конвертера-заглушки',
      nested: ConverterTestMockFindDto,
      serializer: (_, nested) => nested,
    },
    ironLogic: {
      optional: true,
      label: 'Настройки для конвертера от "Iron Logic"',
      nested: ConverterIconLogicFindDto,
      serializer: (_, nested) => nested,
    },
    accessCards: {
      optional: true,
      label: 'Привязанные карты',
      nested: AccessCardFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    accessCardsCount: {
      optional: true,
      label: 'Количество привязанных карт',
      serializer: yup.number(),
    },
    userGroups: {
      optional: true,
      label: 'Группы пользователей',
      nested: UserGroupFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    room: {
      optional: true,
      label: 'Помещение',
      nested: CatalogDtosKeysEnum.ROOM_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    lastOperationDate: {
      optional: true,
      label: 'Дата последней операции (команды)',
      serializer: yup.date(),
    },
    lastOperation: {
      label: 'Последняя операция (команда)',
      serializer: yup
        .string()
        .oneOf(Object.values(IronLogicOperationEnum)),
    },
    timeZones: {
      label: 'Временные зоны',
      nested: ConverterTimeZoneSingleFormDto,
      serializer: (_, nested) => yup.array(nested),
    },
    operatingMode: {
      optional: true,
      label: 'Расписание режимов',
      serializer: yup
        .string()
        .oneOf(Object.values(ConverterOperatingModeEnum)),
    },
    cameras: {
      optional: true,
      label: 'Камеры',
      nested: CctvDtoKeysEnum.HIKVISION_CAMERA_EVENT_FIND_DTO,
      serializer: (_, nested) => yup.array(nested),
    },
  },
);
